import { Box, Checkbox, Radio } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { questionTypeEnum } from '../../../../constants';
import { FormQuestionOption, UpdateFormQuestionOptionBody } from '../../../../services/apiSLTP/qhse/forms/formQuestionOptions';
import CustomTextInput from '../../../ui/CustomTextInput';
import Icon from '../../../ui/Icon';
import IconButton from '../../../ui/IconButton';

interface OptionItemProps {
    option: FormQuestionOption;
    index: number;
    questionType: number;
    localOptions: FormQuestionOption[];
    moveOption: (dragIndex: number, hoverIndex: number) => void;
    onUpdateOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onDeleteOption: (optionId: string) => void;
    onDrop: (newOrder: number) => void;
}

const OptionItem: React.FC<OptionItemProps> = ({ option, index, questionType, moveOption, onUpdateOption, onDeleteOption, onDrop, localOptions }) => {
    const ref = useRef<HTMLDivElement>(null);
    const dragHandleRef = useRef<HTMLDivElement>(null);
    const [value, setValue] = useState(option.value);
    const [hover, setHover] = useState(false);

    const [{ isOverCurrent }, drop] = useDrop({
        accept: 'OPTION',
        collect: (monitor) => ({
            isOverCurrent: monitor.isOver({ shallow: true }),
        }),
        hover: (item: { index: number }) => {
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;
            moveOption(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
        drop: () => {
            onDrop(index);
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'OPTION',
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    useEffect(() => {
        if (ref.current) preview(ref.current);
    }, [preview]);

    drag(dragHandleRef);
    drop(ref);

    return (
        <Box
            ref={ref}
            display="flex"
            alignItems="center"
            flexDirection="row"
            width="100%"
            gap={1}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            sx={{ opacity: isDragging ? 0 : 1 }}
        >
            <Box ref={dragHandleRef} sx={{ cursor: 'move', opacity: hover ? 1 : 0, transition: 'opacity 0.2s' }}>
                <Icon name="dragIndicator" />
            </Box>
            {questionType === questionTypeEnum.SINGLE_CHOICE ? <Radio disabled /> : <Checkbox disabled />}
            <Box maxWidth={300} display={'flex'} width={'100%'}>
                <CustomTextInput
                    value={value}
                    onChange={(text) => {
                        setValue(text);
                        onUpdateOption(option._id, { value: text });
                    }}
                />
            </Box>
            {hover && (
                <Box sx={{ px: 1, display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
                    <IconButton onClick={() => onDeleteOption(option._id)} iconName="deleteForever" />
                    <IconButton onClick={() => onDrop(option.order - 1)} iconName="arrowUpward" disabled={index === 0} />
                    <IconButton onClick={() => onDrop(option.order + 1)} iconName="arrowDownward" disabled={index === localOptions.length - 1} />
                </Box>
            )}
        </Box>
    );
};

export default OptionItem;
